import { useState, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { AlertTriangle } from "lucide-react"

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from "@f2e/ui/components/base"

import { useToast } from "@/hooks"
import { useGetClientChannelsApi, usePublishHistoryApi } from "@/hooks/api"
import { Agent, AgentHistory, ClientChannelType } from "@/types"
import { FetchStatus } from "@/hooks/useFetch"
import {
  getApiErrorPayloadMessage,
  getUserFacingApiErrorMessage,
} from "@/lib/apiErrors"

interface AgentHistoryPublishDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  agent: Agent
  currentHistory?: AgentHistory
  organizationId?: string
  workspaceId?: string
  onPublishComplete?: () => void
}

const AgentHistoryPublishDialog = ({
  open,
  onOpenChange,
  agent,
  currentHistory,
  organizationId = "",
  workspaceId = "",
  onPublishComplete,
}: AgentHistoryPublishDialogProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { alertToast } = useToast()

  const [publishProgress, setPublishProgress] = useState(0)
  const [publishComplete, setPublishComplete] = useState(false)
  const [requiresChannelSetup, setRequiresChannelSetup] = useState(false)
  const [isCheckingChannels, setIsCheckingChannels] = useState(false)
  const [originalUnpublishMode, setOriginalUnpublishMode] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const resolvedWorkspaceId = workspaceId || agent.workspaceId || ""

  const {
    error: publishHistoryError,
    data: publishHistoryRespData,
    status: publishHistoryStatus,
    fetchFn: publishHistory,
  } = usePublishHistoryApi(agent.id)
  const { fetchFn: getClientChannels } = useGetClientChannelsApi(
    organizationId,
    resolvedWorkspaceId
  )

  // Determine if this is unpublish mode - capture the original state when dialog opens
  const isUnpublishMode = useMemo(
    () => originalUnpublishMode,
    [originalUnpublishMode]
  )
  const exposesTabularData = useMemo(
    () =>
      currentHistory?.unifiedTools?.some(
        (tool) => tool.type === "knowledge_base" || tool.type === "tabular_data"
      ) ?? false,
    [currentHistory?.unifiedTools]
  )

  // Reset state when dialog opens/closes
  // We intentionally don't include currentHistory?.wasPublished in dependencies
  // because we only want to capture the original value when dialog opens,
  // not re-capture it when the state changes during the operation
  useEffect(() => {
    if (open) {
      setPublishProgress(0)
      setPublishComplete(false)
      setRequiresChannelSetup(false)
      setIsCheckingChannels(false)
      // Capture the original unpublish mode when dialog opens
      setOriginalUnpublishMode(currentHistory?.wasPublished === true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Error handling for publishing/unpublishing history
  useEffect(() => {
    if (!publishHistoryError) return
    alertToast({
      title: isUnpublishMode
        ? t("workspace.agent.message.unpublishFailed.title")
        : t("workspace.agent.message.publishFailed.title"),
      description: getUserFacingApiErrorMessage(
        publishHistoryError,
        t,
        publishHistoryRespData?.message ||
          (isUnpublishMode
            ? t("workspace.agent.message.unpublishFailed.title")
            : t("workspace.agent.message.publishFailed.title"))
      ),
    })
  }, [
    publishHistoryError,
    publishHistoryRespData,
    alertToast,
    t,
    isUnpublishMode,
  ])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const clearCloseTimer = () => {
    if (!closeTimerRef.current) return
    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }

  const scheduleAutoClose = () => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      onOpenChange(false)
      setPublishComplete(false)
      setPublishProgress(0)
      setRequiresChannelSetup(false)
      closeTimerRef.current = null
    }, 2500)
  }

  const shouldPromptChannelSetup = async () => {
    if (!organizationId || !resolvedWorkspaceId) return false

    const [boundChannelsResp, workspaceChannelsResp] = await Promise.all([
      getClientChannels({
        searchParams: new URLSearchParams({
          limit: "1",
          offset: "0",
          agent_id: agent.id,
        }),
      }),
      getClientChannels({
        searchParams: new URLSearchParams({
          limit: "1",
          offset: "0",
          type: ClientChannelType.WEB,
        }),
      }),
    ])

    const hasBoundChannel = Boolean(boundChannelsResp?.data?.length)
    const hasWebChannel = Boolean(workspaceChannelsResp?.data?.length)

    return !hasBoundChannel && !hasWebChannel
  }

  const handleAction = async () => {
    if (!agent || !currentHistory?.id) return

    setPublishProgress(0)
    setPublishComplete(false)
    setRequiresChannelSetup(false)

    try {
      const resp = await publishHistory({
        jsonBody: { history_id: currentHistory.id, unpublish: isUnpublishMode },
      })
      if (!resp || resp.error_code !== 0) {
        alertToast({
          title: isUnpublishMode
            ? t("workspace.agent.message.unpublishFailed.title")
            : t("workspace.agent.message.publishFailed.title"),
          description: getApiErrorPayloadMessage(
            resp,
            t,
            isUnpublishMode
              ? t("workspace.agent.message.unpublishFailed.title")
              : t("workspace.agent.message.publishFailed.title")
          ),
        })
        return
      }

      let promptChannelSetup = false
      if (!isUnpublishMode) {
        setIsCheckingChannels(true)
        try {
          promptChannelSetup = await shouldPromptChannelSetup()
        } catch (error) {
          console.error("Failed to check client channels after publish", error)
          promptChannelSetup = true
        } finally {
          setIsCheckingChannels(false)
        }
      }

      setPublishComplete(true)
      setRequiresChannelSetup(promptChannelSetup)
      onPublishComplete?.()
      if (!promptChannelSetup) {
        scheduleAutoClose()
      }
    } catch {
      setIsCheckingChannels(false)
    }
  }

  const handleClose = () => {
    clearCloseTimer()
    onOpenChange(false)
  }

  const handleSetupChannel = () => {
    if (!resolvedWorkspaceId) return
    clearCloseTimer()

    const params = new URLSearchParams({
      tab: "channels",
      intent: "create-channel",
      agentId: agent.id,
    })
    onOpenChange(false)
    navigate(`/workspaces/${resolvedWorkspaceId}?${params.toString()}`)
  }

  // Derive isProcessing from the API hook status
  const isProcessing =
    publishHistoryStatus === FetchStatus.Loading || isCheckingChannels

  // Dynamic content based on mode
  const dialogTitle = publishComplete
    ? isUnpublishMode
      ? t("workspace.agent.unpublish.dialog.complete.title")
      : t("workspace.agent.publish.dialog.complete.title")
    : isUnpublishMode
      ? t("workspace.agent.unpublish.dialog.confirm.title")
      : t("workspace.agent.publish.dialog.confirm.title")

  const dialogDescription = publishComplete
    ? isUnpublishMode
      ? t("workspace.agent.unpublish.dialog.complete.description", {
          name: agent.name,
        })
      : requiresChannelSetup
        ? t("workspace.agent.publish.dialog.channelSetup.description", {
            name: agent.name,
          })
        : t("workspace.agent.publish.dialog.complete.description", {
            name: agent.name,
          })
    : isUnpublishMode
      ? t("workspace.agent.unpublish.dialog.confirm.description", {
          name: agent.name,
        })
      : t("workspace.agent.publish.dialog.confirm.description", {
          name: agent.name,
        })

  const progressTitle = isUnpublishMode
    ? t("workspace.agent.unpublish.dialog.progress.title")
    : t("workspace.agent.publish.dialog.progress.title")

  const successMessage = isUnpublishMode
    ? t("workspace.agent.unpublish.dialog.complete.successMessage", {
        version: currentHistory?.versionNumber
          ? `v${currentHistory.versionNumber}`
          : "",
      })
    : t("workspace.agent.publish.dialog.complete.successMessage", {
        version: currentHistory?.versionNumber
          ? `v${currentHistory.versionNumber}`
          : "",
      })

  const actionButtonText = isUnpublishMode
    ? t("workspace.agent.publish.dialog.buttons.unpublish")
    : t("workspace.agent.publish.dialog.buttons.publish")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-[425px] ${isUnpublishMode && publishComplete ? "border-orange-200 dark:border-orange-800" : ""}`}
      >
        <DialogHeader>
          <DialogTitle
            className={
              isUnpublishMode && publishComplete
                ? "text-orange-700 dark:text-orange-300"
                : ""
            }
          >
            {dialogTitle}
          </DialogTitle>
          <DialogDescription
            className={
              isUnpublishMode && publishComplete
                ? "text-orange-600 dark:text-orange-400"
                : ""
            }
          >
            {dialogDescription}
          </DialogDescription>
        </DialogHeader>

        {!publishComplete && (
          <div className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {t("workspace.agent.publish.dialog.details.title")}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("workspace.agent.publish.dialog.details.name")}
                </span>
                <span className="font-medium">{agent.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("workspace.agent.publish.dialog.details.type")}
                </span>
                <span className="font-medium capitalize">{agent.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("workspace.agent.publish.dialog.details.version")}
                </span>
                <span className="font-medium">
                  {currentHistory?.versionNumber
                    ? t("workspace.agent.history.ui.versionNumber", {
                        number: currentHistory.versionNumber,
                      })
                    : t("workspace.agent.history.loading.versions")}
                </span>
              </div>
              {isUnpublishMode && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("workspace.agent.unpublish.dialog.details.status")}
                  </span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">
                    {t(
                      "workspace.agent.unpublish.dialog.details.currentlyPublished"
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {!isUnpublishMode &&
          !publishComplete &&
          !isProcessing &&
          exposesTabularData && (
            <Alert className="border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
              <AlertTriangle
                aria-hidden="true"
                className="size-4 text-amber-700 dark:text-amber-300"
              />
              <AlertTitle>
                {t("workspace.agent.publish.dialog.tabularDataWarning.title")}
              </AlertTitle>
              <AlertDescription className="text-amber-900 dark:text-amber-200">
                {t(
                  "workspace.agent.publish.dialog.tabularDataWarning.description"
                )}
              </AlertDescription>
            </Alert>
          )}

        {isProcessing && (
          <div className="py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{progressTitle}</span>
              <span className="text-sm text-muted-foreground">
                {t("workspace.agent.publish.dialog.progress.progress", {
                  progress: publishProgress,
                })}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ease-out ${
                  isUnpublishMode ? "bg-orange-500" : "bg-blue-600"
                }`}
                style={{ width: `${publishProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {publishComplete && (
          <div className="py-4">
            <div
              className={`flex items-center justify-center mb-2 ${
                isUnpublishMode ? "text-orange-600" : "text-green-600"
              }`}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              {successMessage}
            </p>
          </div>
        )}

        <DialogFooter>
          {!isProcessing && !publishComplete && (
            <>
              <Button variant="outline" onClick={handleClose}>
                {t("workspace.agent.publish.dialog.buttons.cancel")}
              </Button>
              <Button
                onClick={handleAction}
                variant={isUnpublishMode ? "destructive" : "default"}
                className={
                  isUnpublishMode
                    ? "bg-orange-600 hover:bg-orange-700 text-white"
                    : ""
                }
              >
                {actionButtonText}
              </Button>
            </>
          )}
          {!isProcessing && publishComplete && requiresChannelSetup && (
            <>
              <Button variant="outline" onClick={handleClose}>
                {t("workspace.agent.publish.dialog.channelSetup.stayHere")}
              </Button>
              <Button onClick={handleSetupChannel}>
                {t("workspace.agent.publish.dialog.channelSetup.setUpChannel")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AgentHistoryPublishDialog
